import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setCompanyLogoUrl } from './uiSlice';
import { fetchReport } from '../functions/fetchReport';
import { syncThemesIfCopySession } from '../functions/copySessionThemes';

const SPECIAL_EMPLOYEE_ID_ORG = 'd70f3637-1f67-4887-83e5-13e026837aaa';

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

function getEmployeeIdFromCustomFields(customFields) {
  if (!customFields || typeof customFields !== 'object') return '';

  const exactKey =
    'EmployeeID(PleaseenteryouremployeeIDinP00format."P"shouldbeincapitalletter)';
  if (customFields[exactKey]) return customFields[exactKey];

  for (const [key, value] of Object.entries(customFields)) {
    if (String(key).toLowerCase().startsWith('employeeid') && value) {
      return value;
    }
  }
  return '';
}

function cleanNamePart(value) {
  if (value == null) return '';
  const s = String(value).trim();
  return s.toLowerCase() === 'null' ? '' : s;
}

async function fetchGameover({ userId, email, sessionId, organizationId }) {
  if (!userId || !sessionId || !organizationId) return 0;
  try {
    const data = await fetchReport({
      userId,
      email,
      token: `${sessionId}&${organizationId}`,
    });
    if (!data?.success || !data?.report) return 0;
    const g = data.report.gameover;
    const go = g == null || g === '' ? 0 : Number(g);
    return Number.isFinite(go) ? go : 0;
  } catch {
    return 0;
  }
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      // Testing loophole: ?isMultiplaySession=true in the login URL lets the user replay.
      const isMultiplaySessionFromUrl = queryParams.get('isMultiplaySession') === 'true';

      if (save) {
        const stored = localStorage.getItem('userData');
        if (!stored) return rejectWithValue('No saved user data found.');
        const userData = JSON.parse(stored);
        // Preserve replay flag across saved reloads (persisted value OR URL override).
        userData.isMultiplaySession = userData.isMultiplaySession === true || isMultiplaySessionFromUrl;
        dispatch(setSession({ sessionId: userData.sessionId, organizationId: userData.organizationId }));
        try {
          await syncThemesIfCopySession({
            sessionId: userData.sessionId,
            organizationId: userData.organizationId,
            isCopy: userData.isCopy,
            originalSessionId: userData.originalSessionId,
          });
        } catch (syncErr) {
          console.error('syncThemesIfCopySession (save):', syncErr);
        }
        dispatch(fetchThemeData({ themeId: null }));
        userData.gameover = await fetchGameover({
          userId: userData.userId,
          email: userData.email,
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
        });
        localStorage.setItem('userData', JSON.stringify(userData));
        return userData;
      }

      let userResponseData;
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demobypass',
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const otp = queryParams.get('otp');
        const role = queryParams.get('role');
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        let source = 'ORG_USER';
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          source = 'GUEST_USER';
        }

        const headers = {
          'client-secret': process.env.REACT_APP_FIREBASE_CLIENT_SECRET,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        console.log('Login API response:', response.data);
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        response.data.data["role"]=source;
        userResponseData = response.data.data;
      }

      // Store session; for duplicate sessions sync themes from original before fetch
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      try {
        await syncThemesIfCopySession({
          sessionId: userResponseData.sessionId,
          organizationId: userResponseData.organizationId,
          isCopy: userResponseData.isCopy,
          originalSessionId: userResponseData.originalSessionId,
        });
      } catch (syncErr) {
        console.error('syncThemesIfCopySession:', syncErr);
      }
      dispatch(fetchThemeData({ themeId: null }));

      let email = userResponseData.email || userResponseData.employeeId || '';
      // Special guest-user org: treat EmployeeID custom field as email
      if (
        userResponseData.role === 'GUEST_USER' &&
        userResponseData.organizationId === SPECIAL_EMPLOYEE_ID_ORG
      ) {
        const employeeId = getEmployeeIdFromCustomFields(userResponseData.userCustomFieldsData);
        if (employeeId) email = employeeId;
      }

      // Build user data to persist
      // Name resolution order:
      //   1) "firstName lastName" when a first name exists
      //   2) the part of the email before "@" (e.g. "rohit1" from "rohit1@x.in")
      //   3) employeeId
      //   4) empty string
      const emailLocalPart = cleanNamePart((email || '').split('@')[0]);
      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        token: userResponseData.token || "",
        name: [cleanNamePart(userResponseData.firstName), cleanNamePart(userResponseData.lastName)]
          .filter(Boolean)
          .join(' ')
          || emailLocalPart
          || cleanNamePart(userResponseData.employeeId)
          || '',
        email,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        source: demo ? 'DEMO' : 'ORG_USER',
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role:userResponseData.role,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        // Prefer platformBackLink from the login API; fall back to base URL.
        backButtonRedirect: userResponseData.platformBackLink || process.env.REACT_APP_BASE_URL,
        userPlayedCount:userResponseData.userPlayedCount,
        gameover: 0,
        isCopy: !!userResponseData.isCopy,
        originalSessionId: userResponseData.originalSessionId || null,
        // Replay allowed if login API returns the flag OR URL testing override is set.
        isMultiplaySession: userResponseData.isMultiplaySession === true || isMultiplaySessionFromUrl,
      };

      // Fetch organization details if not demo
      if (!demo) {
        const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`);
         console.log(orgRes);
        if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
          console.log(orgRes.data);
          console.log(orgRes.data?.organization.companyLogo);
          if(orgRes.data?.organization.companyLogo=="" || orgRes.data?.organization.companyLogo==null){
            userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
          }else{
            userData.companyLogoUrl = `${process.env.REACT_APP_S3BUCKET_URL}/${orgRes.data.organization.companyLogo}`;
          }
          userData.backButtonRedirect = userResponseData.platformBackLink
            || `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
          console.log("setcompany url",userData.companyLogoUrl);
        }

        userData.gameover = await fetchGameover({
          userId: userData.userId,
          email: userData.email,
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
        });
      }
      
      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      localStorage.setItem('userData', JSON.stringify(userData));
      return userData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.err?.message || error.message);
    }
  }
);
const persisted = localStorage.getItem('userData');
const initialUser = persisted ? JSON.parse(persisted) : null;

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,         // <-- hydrate from storage
    status: initialUser ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      localStorage.removeItem('userData');
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
